/**
 * @packageDocumentation
 * @module HackerAPI.Auth
 */
import { __extends } from "tslib";
import HackerAPIFetch from "../hackerapi-fetch/hackerapi-fetch";
import Base from "../base/base";
import { parseDate } from "../utils/utils";
/**
 * A container for authentication data with static methods for interfacing with hackerapi
 * @class
 * @extends { HackerAPI.Base }
 */
var Auth = /** @class */ (function (_super) {
    __extends(Auth, _super);
    /**
     * Creates an Auth instance. You shouldn't be calling this directly.
     * @private
     * @param auth
     */
    function Auth(_a) {
        var name = _a.name, email = _a.email, id = _a.id, expires = _a.expires, token = _a.token, token_jwt = _a.token_jwt, timestamp = _a.timestamp;
        var _this = _super.call(this) || this;
        /**
         * The name of the authenticated user
         * @type { string }
         */
        _this.name = name;
        /**
         * The email of the authenticated user
         * @type { string }
         */
        _this.email = email;
        /**
         * The user id of the authenticated user
         * @type { string }
         */
        _this.id = id;
        /**
         * When the token expires
         * @type { Date }
         */
        _this.expires = parseDate(expires);
        /**
         * The token to use for subsequent events
         * @type { string }
         */
        if (token !== undefined) {
            Auth.token = token;
        }
        /**
         * @type { string }
         */
        _this.tokenJwt = token_jwt;
        /**
         * When the timestamp was issued
         * @type { Date }
         */
        _this.timestamp = parseDate(timestamp);
        return _this;
    }
    Object.defineProperty(Auth, "token", {
        /**
         * Gets the default token
         * @returns
         */
        get: function () {
            return Auth._token;
        },
        /**
         * Sets the default token to use in requests
         * @param token
         * @param [token.token] - Only if token is an object
         * @todo Error throwing if invalid type
         */
        set: function (t) {
            Auth._token = t;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Auth.prototype, "token", {
        get: function () {
            return Auth.token;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gets a token to use with all subsequent authenticated requests
     * @param email
     * @param password
     * @param [firebase_service]
     * @returns
     */
    Auth.login = function (email, password, firebase_service) {
        return HackerAPIFetch({
            path: "/v2/auth/login",
            method: "POST",
            body: {
                email: email,
                password: password,
                firebase_service: firebase_service
            }
        }).then(function (data) { return new Auth(data); });
    };
    /**
     * Gets a token to use with all subsequent authenticated requests
     * @param Third party web token
     * @returns
     */
    Auth.loginJWT = function (jwt) {
        return HackerAPIFetch({
            path: "/v2/auth/login",
            method: "POST",
            body: {
                jwt: jwt
            }
        }).then(function (data) { return new Auth(data); });
    };
    /**
     * Checks to see if a given email exists in the HackerAPI
     * @param { string | HackerAPI.User} email
     * @param [email.email] - If an object is passed in
     * @returns
     */
    Auth.checkEmail = function (email) {
        if (email && typeof email === "object" && email.email) {
            email = email.email;
        }
        return HackerAPIFetch({
            path: "/v2/auth/check",
            body: { email: email },
            method: "POST"
        }).then(function (data) { return data.exists || false; });
    };
    /**
     * Triggers an email to reset your password
     * @param email
     * @param callbackUrl
     * @param tokenOnly
     * @returns
     */
    Auth.resetPassword = function (email, callbackUrl, tokenOnly) {
        if (tokenOnly === void 0) { tokenOnly = false; }
        return HackerAPIFetch({
            path: "/v2/auth/password",
            method: "POST",
            body: {
                email: email,
                token_only: tokenOnly,
                callback_url: callbackUrl
            }
        }).then(function (data) {
            if (data.token_hash) {
                data.tokenHash = data.token_hash;
                delete data.token_hash;
            }
            return data;
        });
    };
    /**
     * Sets the user's password
     * @param email
     * @param password
     * @param token
     * @returns
     */
    Auth.setPassword = function (email, password, token) {
        return HackerAPIFetch({
            path: "/v2/auth/password",
            method: "PUT",
            body: {
                email: email,
                password: password,
                token: token
            }
        });
    };
    return Auth;
}(Base));
export default Auth;
//# sourceMappingURL=auth.js.map